import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../auth/AuthContext";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import "./EditDealPage.css";

// Convert any date value (ISO timestamp or YYYY-MM-DD) to the format HTML date inputs require
const toDateInputValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  try {
    return new Date(value).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const EditDealPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    title: "",
    amount: "",
    stage: "prospecting",
    category: "regular",
    close_date: "",
    company_id: "",
    contact_ids: [],
    collab_ids: [],
  });

  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const initialFormRef = useRef(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const isDirty = initialFormRef.current !== null && JSON.stringify(form) !== initialFormRef.current;

  // Warn on browser tab close/refresh if there are unsaved changes (item #16).
  // react-router v7 with plain BrowserRouter has no useBlocker, so in-app
  // navigation away from this page is guarded via the Back/Cancel buttons instead.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const goBack = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate(`/deal-details/${id}`);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError("");
      try {
        const [dealRes, companiesRes, customersRes, stagesRes, usersRes] = await Promise.all([
          api.get(`/api/deals/${id}`),
          api.get("/api/companies/", { params: { limit: 500 } }),
          api.get("/api/customers/", { params: { limit: 500 } }),
          api.get('/api/stages/'),
          api.get("/api/admin/users/").catch(() => ({ data: [] })), // fallback if not admin
        ]);

        const dealData = dealRes.data;

        // Check if user has permission to edit: only admin or owner
        if ((user?.role !== 'admin') && user?.id !== dealData.created_by) {
          setError("You don't have permission to edit this deal.");
          setLoading(false);
          return;
        }

        setCompanies(companiesRes.data || []);
        setCustomers(customersRes.data || []);
        setStages(stagesRes.data || []);
        setUsers(usersRes.data || []);
        setForm({
          title: dealData.title || "",
          amount: dealData.amount ?? "",
          stage: dealData.stage || "prospecting",
          category: dealData.category || "regular",
          close_date: toDateInputValue(dealData.close_date),
          company_id: dealData.company_id || "",
          contact_ids: (dealData.contacts || []).map((c) => c.id),
          collab_ids: dealData.collab_ids || [],
        });
        initialFormRef.current = JSON.stringify({
          title: dealData.title || "",
          amount: dealData.amount ?? "",
          stage: dealData.stage || "prospecting",
          category: dealData.category || "regular",
          close_date: toDateInputValue(dealData.close_date),
          company_id: dealData.company_id || "",
          contact_ids: (dealData.contacts || []).map((c) => c.id),
          collab_ids: dealData.collab_ids || [],
        });
      } catch (e) {
        console.error(e);
        setError("Failed to load deal or related data.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAll();
  }, [id, user]);

  const selectedCompanyId = form.company_id;
  const filteredCustomers = useMemo(() => {
    if (!selectedCompanyId) return [];
    return (customers || []).filter((c) => c.company_id === selectedCompanyId);
  }, [customers, selectedCompanyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyChange = (e) => {
    const company_id = e.target.value;
    setForm((prev) => ({
      ...prev,
      company_id,
      // reset contacts when company changes (prevents mismatched contacts)
      contact_ids: [],
    }));
  };

  const handleContactsChange = (e) => {
    const values = Array.from(e.target.selectedOptions).map((o) => o.value);
    setForm((prev) => ({ ...prev, contact_ids: values }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        title: form.title,
        amount: parseFloat(form.amount),
        stage: form.stage,
        category: form.category,
        close_date: form.close_date || null,
        company_id: form.company_id,
        contact_ids: form.contact_ids || [],
        collab_ids: form.collab_ids || [],
      };

      await api.put(`/api/deals/${id}`, payload);
      initialFormRef.current = JSON.stringify(form);
      navigate(`/deal-details/${id}`);
    } catch (e) {
      console.error(e);
      if (e.response) {
        // Server returned an explicit HTTP error (4xx/5xx) — genuine failure
        const msg = e.response?.data?.detail || "Failed to save changes.";
        setError(msg);
      } else {
        // Network/CORS error: the server processed the save but dropped the connection.
        // Navigate to deal details so the user sees the saved state.
        navigate(`/deal-details/${id}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-deal-page">
        <div className="edit-deal-container">
          <div className="edit-deal-card">
            <div className="edit-deal-title">Edit Deal</div>
            <div className="edit-deal-subtitle">Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-deal-page">
      <div className="edit-deal-container">
        <div className="edit-deal-card">
          <div className="edit-deal-header">
            <div>
              <div className="edit-deal-title">Edit Deal</div>
              <div className="edit-deal-subtitle">
                Update deal details, ownership, and contacts.
              </div>
            </div>

            <button
              className="edit-deal-back"
              type="button"
              onClick={goBack}
            >
              Back
            </button>
          </div>

          {error && <div className="edit-deal-error">{error}</div>}

          <form className="edit-deal-form" onSubmit={handleSubmit}>
            <div className="edit-grid">
              <div className="edit-field">
                <label>Deal Name *</label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Enter deal name"
                  required
                />
              </div>

              <div className="edit-field">
                <label>Amount *</label>
                <input
                  name="amount"
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="edit-field">
                <label>Stage *</label>
                <select 
                  name="stage" 
                  value={form.stage} 
                  onChange={handleChange} 
                  required
                  disabled={["closed_won", "closed_lost"].includes(form.stage)}>
                  {stages.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                  {form.stage && !stages.find(s => s.key === form.stage) && (
                    <option key={form.stage} value={form.stage} disabled>
                      {form.stage} (Current - Invalid)
                    </option>
                  )}
                </select>
              </div>

              <div className="edit-field">
                <label>Category *</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                >
                  <option value="regular">Regular</option>
                  <option value="federal">Federal</option>
                </select>
              </div>

              <div className="edit-field">
                <label>Expected Close</label>
                <input
                  name="close_date"
                  type="date"
                  value={form.close_date}
                  onChange={handleChange}
                />
              </div>

              <div className="edit-field">
                <label>Company *</label>
                <select
                  name="company_id"
                  value={form.company_id}
                  onChange={handleCompanyChange}
                  required
                >
                  <option value="">Select a company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="edit-field">
                <label>Collaborator</label>
                <select
                  value={form.collab_ids[0] || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      collab_ids: e.target.value ? [e.target.value] : [],
                    }))
                  }
                >
                  <option value="">No collaborator</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name || u.username || u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="edit-field edit-field-full">
                <label>Contacts</label>
                <select
                  multiple
                  value={form.contact_ids}
                  onChange={handleContactsChange}
                  className="multi-select"
                  disabled={!form.company_id}
                >
                  {filteredCustomers.length === 0 ? (
                    <option value="" disabled>
                      {form.company_id ? "No contacts for this company" : "Select a company first"}
                    </option>
                  ) : (
                    filteredCustomers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.email ? ` — ${c.email}` : ""}
                      </option>
                    ))
                  )}
                </select>
                <div className="edit-hint">
                  Hold <b>Ctrl</b> (Windows) / <b>Cmd</b> (Mac) to select multiple contacts.
                </div>
              </div>
            </div>

            <div className="edit-actions">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button
                className="cancel-button"
                type="button"
                onClick={goBack}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmDialog
        open={showUnsavedModal}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
        confirmLabel="Leave without saving"
        cancelLabel="Stay"
        variant="danger"
        onConfirm={() => {
          setShowUnsavedModal(false);
          navigate(`/deal-details/${id}`);
        }}
        onCancel={() => setShowUnsavedModal(false)}
      />
    </div>
  );
};

export default EditDealPage;
